import React, { createContext, useEffect, useReducer, useState, useRef } from 'react'
import { SSE } from 'sse.js'
import { fetchSearch, getProductSearch, getSessionUser, getSoftwarePreferences } from '../api'
import { makeProductDetailURL } from '@insight/toolkit-react/lib/PDPModal/PDPHelpers'
import { getCurrentLocale, getDefaultLoggedOutSalesOrg } from '@insight/toolkit-utils/lib/helpers'
import { t } from '@insight/toolkit-utils/lib/labels'
import { isHybridXEnabled, addFieldsForHybridX } from 'app-api-user-service'
import {
  fetchAEMContent,
  fetchContentSearch,
  fetchSolutionsAndServices,
} from '../api/getData'
import { ILISTVIEW, INSIGHT_CURRENT_LOCALE_COOKIE_NAME, CONTENTMAP, PAGE_TYPE } from '../constants'
import contentURL from '../shared/aemContentUrl'
import { splitFacets } from '../shared/utils'

const locale = getCurrentLocale(INSIGHT_CURRENT_LOCALE_COOKIE_NAME)
const isInstockOnlyFlagEnabled =
  window.flags && window.flags['GNA-9998-IN-STOCK']
const gridViewOnDefault = window.flags && window.flags['GNA-17775_GRID_VIEW_SEARCH_RESULT']
const productView = gridViewOnDefault ? ILISTVIEW.grid : ILISTVIEW.list;
const initialState = {
  country: 'US',
  currency: 'USD',
  instockOnly: isInstockOnlyFlagEnabled ? 'false' : 'true',
  lang: 'en_US',
  salesOrg: getDefaultLoggedOutSalesOrg(locale),
  products: [],
  contents: {},
  numFound: 1,
  start: 0,
  rows: 50,
  view: productView,
  customSort: 'best_match',
  pages: 1,
  pageNumber: 1,
  facets: [],
  origFacetDisplays: {},
  selectedFilters: new Map(),
  isSearching: true,
  query: '',
  origParams: {},
  noResultsFound: false,
  signalMetaData: {},
  docs: [],
  contentData: {},
  searchInfo: {}
}

const searchSourceMap = {
  k: 'search',
  h: 'search',
  s: 'recommendation',
}

const ALLOWED_SEARCH_PAYLOAD_PARAMS = [
  'country',
  'q',
  'salesOrg',
  'lang',
  'locale',
  'category',
  'pq',
  'qsrc',
  'qtype',
  'searchType',
  'brand',
  'selectedFacet',
  'rangeFacet',
  'customSort',
  'instockOnly',
  'start',
  'rows',
  'tabType',
  'selectedContractIDs',
]

export const SearchContext = createContext(initialState)

export function SearchProvider({ children }) {
  const [
    {
      originalSpelling,
      correctedSpelling,
      country,
      currency,
      fusionQueryId,
      instockOnly,
      lang,
      salesOrg,
      products,
      contents,
      docs,
      numFound,
      start,
      rows,
      view,
      customSort,
      origFacetDisplays,
      pages,
      pageNumber,
      originalFilters,
      selectedFilters,
      searchSource,
      facets,
      isSearching,
      error,
      query,
      qsrc,
      origParams,
      outOfStockOnly,
      noResultsFound,
      signalMetaData,
      contentData,
      searchInfo
    },
    dispatch,
  ] = useReducer(reducer, initialState);
  const [response, setResponse] = useState(null)
  const [priceInfo, setPriceInfo] = useState(null)
  const [searchSSEComplete, setSearchSSEComplete] = useState(false)
  const [searchSSEError, setSearchSSEError] = useState(false)
  const skipNextSearchTabsTracking = useRef(false)
  const params = new URLSearchParams(window?.location?.search);
  const [tabType, setTabtype] = useState(params.get('tabType') || 'products');
  const solutionsAndServicesPrevState = useRef({
    selectedFilters: new Map(),
    origFacetDisplays: {},
    origParams: {},
  })
  useEffect(() => {
    processProducts()
  }, [response, docs])

  function setSearchSource(payload) {
    dispatch({ type: 'SET_SEARCH_SOURCE', payload })
  }

  // Returns the current value of the GNA-9998-IN-STOCK feature flag from window.flags
  const getInStockFlagValue = () => {
    return window?.flags && window?.flags['GNA-9998-IN-STOCK']
  }
  const processParams = (queryString, contents, salesOrg, isLoggedIn, isInStockUserPreference) => {
    const params = new URLSearchParams(queryString)
    const country = locale.split('_')[1]
    // store search source in state so that we can update signals
    if (params.has('qsrc')) {
      setSearchSource({
        searchSource: searchSourceMap[params.get('qsrc')],
        qsrc: params.get('qsrc'),
        salesOrg,
      })
    } else if (params.has('searchType')) {
      setSearchSource({ searchSource: params.get('searchType'), salesOrg })
    }
    // add default params if they are not in initial query string
    if (!params.has('country')) params.set('country', country)

    if (!params.has('instockOnly')) {
      const isInStockFeatureFlagEnabled = getInStockFlagValue()
      if (isLoggedIn) {
        params.set('instockOnly', isInStockUserPreference || (isInStockFeatureFlagEnabled ? false : true))
      } else {
        params.set('instockOnly', isInStockFeatureFlagEnabled ? 'false' : 'true')
      }
    }

    if (!params.has('lang'))
      params.set('lang', locale === 'en_CA' ? 'en_US' : locale)
    if (!params.has('locale')) params.set('locale', locale)
    if (!params.has('rows')) params.set('rows', '50')
    if (!params.has('start')) params.set('start', '0')
    if (!params.has('salesOrg')) params.set('salesOrg', salesOrg)
    if (params.has('licenseContractIds')) {
      params.set('selectedContractIDs', params.get('licenseContractIds'))
    }

    // Build payload params from explicit whitelist only.
    const payloadParams = new URLSearchParams()
    ALLOWED_SEARCH_PAYLOAD_PARAMS.forEach((key) => {
      if (params.has(key)) {
        payloadParams.set(key, params.get(key))
      }
    })

    return payloadParams
  }

  // handle redirect and fire tracking events and redirectUrl exist in search response
  const redirectAndTrack = (redirectURL, params) => {
    if (!redirectURL) return;
    const query = params.get('q') || '';
    const querySRC = params.get('qsrc') || '';
    const searchTypeMap = {
      k: "Typed In",
      h: "History",
      s: "Suggested",
      p: "Suggested - Product",
      "": "NA"
    };
    const searchType = searchTypeMap[querySRC] || "Typed In";
    const queryInfo = {
      queryKeyword: query,
      queryType: searchType,
      queryRedirect: redirectURL
    };

    // Fire tracking events
    fireTagEvent("queryInfo", queryInfo);
    localStorage.setItem('search-type', searchType);
    window["_satellite"] && _satellite.track && _satellite.track("queryInfo");
    window.location = redirectURL;
  };

  const searchProducts = async (queryString, contents, salesOrg, contract) => {
    // if (!inputString || inputString === query) return;
    try {
      dispatch({ type: 'INITIATED', payload: queryString });

      const {
        isLoggedIn,
        isIpsLogo,
        sessionId,
        userInformation: {
          isCES,
          currencyCode,
          webGroup: { webGroupId: webGroup } = {},
          account: { soldToId: soldTo } = {},
          isIpsUser: loginIpsUser,
          webLoginProfileId: userId,
          UserType: userType,
          permissions: { disableSearchRedirect = false } = {}
        } = {} }
        = await getSessionUser();

      let isInStockUserPreference = false;
      if (isLoggedIn) {
        const softwarePreferences = await getSoftwarePreferences();
        const { displayInStockOnly } = softwarePreferences || {};
        isInStockUserPreference = displayInStockOnly;
      }
      const params = processParams(queryString, contents, salesOrg, isLoggedIn, isInStockUserPreference);

      const ignoreRedirectParams = (disableSearchRedirect && window.flags['GNA-17145-ENABLE-LUCIDWORKS-REDIRECTS']) || false
      if ((isIpsLogo || loginIpsUser) && !!contract) {
        // if it's logout/login ips and contract is been selected then call eventStream and get data
        const nonLoggedInIpsUser = !isLoggedIn && isIpsLogo
        const ipsUser = nonLoggedInIpsUser || isLoggedIn && loginIpsUser
        const payload = {}

        params.forEach((value, key) => {
          payload[key] = value;
        })
        if (payload['selectedContractIDs']) {
          const selectedContractIDs = payload['selectedContractIDs'];
          payload['selectedContractIDs'] = selectedContractIDs.includes(',') ? selectedContractIDs.split(',') : selectedContractIDs;
        }
        payload['userSegment'] = 'CES'
        payload['sessionId'] = encodeURIComponent(sessionId)

        if (isLoggedIn) {
          if (soldTo && webGroup) {
            payload['userId'] = userId
            payload['soldTo'] = soldTo
            payload['webGroup'] = webGroup
            payload['currencyCode'] = currencyCode
            await addFieldsForHybridX({ isLoggedIn, isCES }, payload, { userType })
          } else {
            payload['userId'] = userId
          }
        }
        payload['returnPrice'] = 'async'
        payload['ignoreRedirect'] = ignoreRedirectParams
        setSearchSSEComplete(false)
        getSearchData(payload, contract)
      } else {
        const res = await (isHybridXEnabled(isLoggedIn, isCES) ? getProductSearch(params) : fetchSearch(params.toString()));
        if (!res) {
          dispatch({ type: 'ERROR_RESET_SEARCH', payload: queryString });
          return;
        }
        const redirectURL = res.data?.redirectUrl;
        if (redirectURL) {
          redirectAndTrack(redirectURL, params)
        }
        setResponse(res.data)
      }
    } catch (e) {
      dispatch({ type: 'ERROR_RESET_SEARCH', payload: queryString });
    }
  }

  const getSearchData = async (payload, contract, enableLWSearchRedirect) => {
    const source = new SSE("/gapi/product-search/search-sse", {
      headers: { 'Content-Type': 'application/json', 'charset': 'utf-8' },
      method: 'POST',
      payload: JSON.stringify({ ...payload, "contractId": contract?.contractNumber, "contractType": contract?.contractName })
    });

    source.addEventListener('readystatechange', e => {
      if (e.readyState === 2) {
        setSearchSSEComplete(true)
      }
    })

    source.addEventListener('productInfo', function (e) {
      const payload = e.data;
      const parsedPayload = JSON.parse(payload);
      if (parsedPayload?.redirectUrl && enableLWSearchRedirect) {
        redirectAndTrack(parsedPayload.redirectUrl, params);
      }
      setResponse(parsedPayload)
    });

    source.addEventListener('prices', function (e) {
      const payload = e.data;
      setPriceInfo(prevData => ({ ...prevData, ...JSON.parse(payload) }))
    });

    source.addEventListener('error', function (e) {
      setSearchSSEError(true)
    });
  }

  const processProducts = () => {
    if (!!response) {
      const data = response
      const {
        products = [],
        fusionQueryId,
        salesOrg,
        numFound,
        origFacetDisplays,
        origParams,
      } = data

      data.signalMetaData = JSON.stringify({
        fusionQueryId,
        origParams,
        origFacetDisplays,
      })
      // store Fusion query ID in session storage, so that PDP page can fire a signal with right fusion query ID
      window.sessionStorage.setItem('fusionQueryId', fusionQueryId)
      window.sessionStorage.setItem('signalMetaData', data.signalMetaData)
      if (products.length === 1) {
        const keyword = params.has('q') && params.get('q')
        const {
          description,
          materialId,
          manufacturerName,
          manufacturerPartNumber,
        } = products[0]

        if (keyword && keyword?.toLowerCase() === materialId?.toLowerCase()) {
          // search by material ID
          // redirect to PDP
          const pdpURL = makeProductDetailURL({
            materialId,
            mfrName: manufacturerName,
            description,
            manufacturerId: manufacturerPartNumber,
          })
          if (!!pdpURL) {
            // during development time, we saw some missing data. which resulted in empty URL
            console.warn(
              'Insufficient data to prepare product URL',
              materialId,
              manufacturerName,
              description,
              manufacturerPartNumber
            )
            window.location.href = pdpURL
          }
        }
      }

      dispatch({ type: 'COMPLETED', payload: { data, contents, salesOrg } })
    } else {
      return null
    }
  }

  const resetSearchInfo = () => {
    dispatch({ type: 'SEARCH_INFO', payload: {
      searchKeyword: origParams?.q,
      searchType: null,
      searchTabType: CONTENTMAP[tabType]?.tab || CONTENTMAP.products.tab,
      resultCounts: {},
      searchFilter: {},
      pageType: null
    } })
  }

  const fireSearchTabsTracking = ({ tabType, searchInfoOverride, skipPerFilterTracking = false }) => {
    const { products: product, brands, articles, solutions, solutionsAndServices } = CONTENTMAP
    const filteredSearchInfo = {
      searchFilter: searchInfo?.searchFilter || {},
    }
    const effectiveSearchInfo = searchInfoOverride || filteredSearchInfo || {}
    const querySrc = qsrc || params.get('qsrc')
    const isWildCardSearch = origParams?.q === '*:*'
    const isCategorySearch = !!origParams?.category && isWildCardSearch
    // populate digital data with search info, only firing this event when there is search term
    const searchType =
      {
        k: 'Typed In',
        h: 'History',
        s: 'Suggested',
        p: 'Suggested - Product',
        '': 'NA',
      }[querySrc] || ''

    const pageType = window.location.pathname.includes('/partner/')
      ? PAGE_TYPE.partner
      : (isCategorySearch ? PAGE_TYPE.brand : PAGE_TYPE.search)

    let nextSearchInfo = {
      ...effectiveSearchInfo,
      searchKeyword: origParams?.q,
      searchType,
      pageType,
      searchTabType: CONTENTMAP[tabType]?.tab || CONTENTMAP.products.tab,
      resultCounts: effectiveSearchInfo.resultCounts || {},
      searchFilter: effectiveSearchInfo.searchFilter || {}
    }

    let deviceType
    if (window.innerWidth <= 568) {
      deviceType = 'Mobile'
    } else if (window.innerWidth > 568 && window.innerWidth <= 1024) {
      deviceType = 'Tablet'
    } else {
      deviceType = 'Desktop'
    }

    if(isWildCardSearch) {
      const { searchKeyword, ...searchInfoWithoutKeyword } = nextSearchInfo
      nextSearchInfo = searchInfoWithoutKeyword
    }

    if (!!response) {
      const data = response
      const { numFound, origFacetDisplays } = data

      if ((origParams?.q && !isWildCardSearch) || isCategorySearch) {
        const contentTilesData = docs?.slice(0, 5)
        const searchTabs = Object.values(CONTENTMAP).map(
          (content) => content.id
        )
        if (searchTabs.indexOf(tabType) < 0) {
          return
        }
        switch (tabType) {
          case product.id:
            nextSearchInfo.resultCounts = {
              [product.counts]: numFound,
            }
            if(!nextSearchInfo?.searchFilter?.predefinedFilterValues) {
              const predefinedFilterValues = [...selectedFilters?.values()]?.map(filter => filter.label || filter.val)
              nextSearchInfo.searchFilter = {
                ...(nextSearchInfo.searchFilter || {}),
                predefinedFilterValues,
              }
            }
            nextSearchInfo.searchFilter = {
              ...(nextSearchInfo.searchFilter || {}),
              origFacetDisplays: origFacetDisplays,
              origParams: origParams,
              totalArticles: contentTilesData?.length,
              deviceType: deviceType
            }
            break
            case solutionsAndServices.id:
              if (!contentData?.solutionsAndServices || contentData?.solutionsAndServices?.isSearching) {
                return
              }
              nextSearchInfo.resultCounts = {
                [solutionsAndServices.counts]:
                  contentData?.solutionsAndServices?.numFound,
              }
              if(!nextSearchInfo?.searchFilter?.predefinedFilterValues && contentData?.solutionsAndServices?.selectedFilters) {
                const predefinedFilterValues = [...(solutionsAndServicesPrevState.current?.selectedFilters?.values() || [])]?.map(filter => filter.label || filter.val)
                nextSearchInfo.searchFilter = {
                  ...(nextSearchInfo.searchFilter || {}),
                  predefinedFilterValues,
                }
              }
              nextSearchInfo.searchFilter = {
                ...(nextSearchInfo.searchFilter || {}),
                origFacetDisplays: contentData?.solutionsAndServices?.origFacetDisplays,
                origParams: contentData?.solutionsAndServices?.origParams,
                deviceType: deviceType
              }
              delete nextSearchInfo.searchFilter?.facetValue
              delete nextSearchInfo.searchFilter?.deselectedFacetValues
              delete nextSearchInfo.searchFilter?.facetType
              delete nextSearchInfo.searchFilter?.['Custom Input Values']
              delete nextSearchInfo.searchFilter?.totalArticles

              const snsFacets = contentData?.solutionsAndServices?.facets || []
              const currentFilters = contentData?.solutionsAndServices?.selectedFilters
              const prevFilters = solutionsAndServicesPrevState.current?.selectedFilters
              const { added, removed } = getSelectedFiltersDiff(
                prevFilters,
                currentFilters,
                snsFacets
              )
              const currentFilterSize = currentFilters?.size || 0
              const prevFilterSize = prevFilters?.size || 0
              const predefinedFilterValues = [...currentFilters?.values()]?.map(filter => filter.label || filter.val)
            switch (true) {
              // When solutions and services tab is loaded for the first time or no filters are selected.
              case (currentFilterSize === 0 && prevFilterSize === 0) || Object.keys(searchInfo || {}).length === 0:
                nextSearchInfo.searchFilter = {
                  ...(nextSearchInfo.searchFilter || {}),
                  predefinedFilterValues: predefinedFilterValues,
                  ...added[0]
                }

                delete nextSearchInfo.searchFilter?.deselectedFacetValues
                delete nextSearchInfo.searchFilter?.facetValue
                delete nextSearchInfo.searchFilter?.facetType
                break
              // When all filters are cleared.
              case currentFilterSize === 0 && prevFilterSize > 0:
                if (!skipPerFilterTracking) {
                  const removedFilters = removed.filter(
                    (filter) => filter?.facetType && filter?.deselectedFacetValues
                  )

                  if (removedFilters.length) {
                    removedFilters.forEach((removedFilter) => {
                      const clearAllSearchInfo = {
                        ...nextSearchInfo,
                        searchFilter: {
                          ...(nextSearchInfo.searchFilter || {}),
                          predefinedFilterValues,
                          facetType: removedFilter.facetType,
                          deselectedFacetValues: removedFilter.deselectedFacetValues,
                        },
                      }
                      const searchInfoSnapshot = JSON.parse(JSON.stringify(clearAllSearchInfo || {}))
                      dispatch({ type: 'SEARCH_INFO', payload: clearAllSearchInfo })
                      window.fireTagEvent('searchInfo', searchInfoSnapshot)
                    })
                    return
                  }
                }
                break
              // When filters are added.
              case (currentFilterSize > prevFilterSize) && added?.length === 1:
                nextSearchInfo.searchFilter = {
                  ...(nextSearchInfo.searchFilter || {}),
                  predefinedFilterValues: predefinedFilterValues,
                  ...added[0]
                }
                break
              // When filters are removed.
              case currentFilterSize < prevFilterSize && removed?.length === 1:
                nextSearchInfo.searchFilter = {
                  ...(nextSearchInfo.searchFilter || {}),
                  predefinedFilterValues: predefinedFilterValues,
                  ...removed[0]
                }
                break
              default:
                break
            }
            break
          case brands.id:
            nextSearchInfo.resultCounts = {
              [brands.counts]: contents?.brands?.total,
            }
            delete nextSearchInfo.searchFilter
            break
          case articles.id:
            nextSearchInfo.resultCounts = {
              [articles.counts]: contents?.articles?.results,
            }
            delete nextSearchInfo.searchFilter
            break
          case solutions.id:
            nextSearchInfo.resultCounts = {
              [solutions.counts]: contents?.solutions?.results,
            }
            delete nextSearchInfo.searchFilter
            break
          default:
            break
        }
        const searchInfoSnapshot = JSON.parse(JSON.stringify(nextSearchInfo || {}))

        dispatch({ type: 'SEARCH_INFO', payload: nextSearchInfo })
        window.fireTagEvent('searchInfo', searchInfoSnapshot)
        // Only reset for non-solutionsAndServices tabs; solutionsAndServices state is managed in searchSolutionsAndServices
        if (tabType !== solutionsAndServices.id) {
          resetSearchInfo()
        }
      }
    }
  }

  const trackSearchTabsData = ({ tabType }) => {
    if (skipNextSearchTabsTracking.current) {
      skipNextSearchTabsTracking.current = false
      return
    }

    fireSearchTabsTracking({ tabType })
  }

  const searchBrands = async ({ searchText, pageSize, currentPage }) => {
    const searchPaths = contentURL('brands', locale)
    const { data } = await fetchAEMContent({
      searchText,
      pageSize,
      currentPage,
      searchPaths,
    })
    return data
  }

  const searchArticles = async ({ searchText, pageSize, currentPage }) => {
    const searchPaths = contentURL('articles', locale)
    const { data } = await fetchAEMContent({
      searchText,
      pageSize,
      currentPage,
      searchPaths,
    })
    return data
  }

  const searchSolutions = async ({ searchText, pageSize, currentPage }) => {
    const searchPaths = contentURL('solutions', locale)
    const { data } = await fetchAEMContent({
      searchText,
      pageSize,
      currentPage,
      searchPaths,
    })
    return data
  }

  const searchSolutionsAndServices = async ({
    queryString,
    tabType
  }) => {
    try {
      solutionsAndServicesPrevState.current = {
        selectedFilters: contentData?.solutionsAndServices?.selectedFilters ? new Map(contentData.solutionsAndServices.selectedFilters) : new Map(),
        origFacetDisplays: contentData?.solutionsAndServices?.origFacetDisplays ? { ...contentData.solutionsAndServices.origFacetDisplays } : undefined,
        origParams: contentData?.solutionsAndServices?.origParams ? { ...contentData.solutionsAndServices.origParams } : undefined,
      }
      dispatch({ type: 'SEARCH_CONTENT', payload: { queryString, tabType, isSearching: true } })

      const params = new URLSearchParams(queryString);
      const country = locale.split('_')[1];
      // store search source in state so that we can update signals
      if (params.has('qsrc')) {
        setSearchSource({
          searchSource: searchSourceMap[params.get('qsrc')],
          qsrc: params.get('qsrc'),
          salesOrg,
        })
      } else if (params.has('searchType')) {
        setSearchSource({ searchSource: params.get('searchType'), salesOrg })
      }
      // add default params if they are not in initial query string
      if (!params.has('country') || !params.get('country')) params.set('country', country)
      if (!params.has('lang') || !params.get('lang')) params.set('lang', locale === 'en_CA' ? 'en_US' : locale)
      if (!params.has('locale') || !params.get('locale')) params.set('locale', locale)
      if (!params.has('rows') || !params.get('rows')) params.set('rows', '50')
      if (!params.has('start') || !params.get('start')) params.set('start', '0')
      if (!params.has('salesOrg') || !params.get('salesOrg')) params.set('salesOrg', salesOrg)


      const { userInformation, isLoggedIn, sessionId: session } = await getSessionUser();
      const userId = userInformation?.cdmUid;
      const soldTo = userInformation?.account?.soldToId;
      const webGroup = userInformation?.webGroup?.webGroupId;
      const userSegment = 'CES';
      const sessionId = encodeURIComponent(session);

      let paramsObj = Object.fromEntries(params);
      const requestParams = {
        ...paramsObj,
        userId,
        userSegment,
        sessionId,
        ...((isLoggedIn && soldTo && webGroup) && {
          soldTo,
          webGroup
        })
      };

      const { data } = await fetchSolutionsAndServices(requestParams);
      const payload = { data, products, salesOrg, tabType, userSegment, sessionId };
      dispatch({ type: 'SET_CONTENTS', payload });
      const {
        origFacetDisplays: { },
        origParams: { },
      } = data ?? {}
      dispatch({
        type: 'SEARCH_INFO',
        payload: {
          origFacetDisplays,
          origParams,
        },
      })
      return data;
    } catch (e) {
      dispatch({ type: 'SEARCH_CONTENT', payload: { tabType } })
      console.warn('Error while fetching solutions and services: ', e);
      return {};
    }
  }

  const searchDocs = async ({ searchText, salesOrg }) => {
    const { data } = await fetchContentSearch({
      searchText,
      locale,
      salesOrg,
    })
    dispatch({ type: 'SET_DOCS', payload: data?.docs || [] })
  }

  const getFilterDataFromMapKey = (mapKey = '') => {
    const separatorIndex = mapKey.indexOf(':')
    if (separatorIndex === -1) {
      return { group: mapKey, val: '' }
    }

    return {
      group: mapKey.slice(0, separatorIndex),
      val: mapKey.slice(separatorIndex + 1),
    }
  }

  const trackFiltersBeforeClearAll = () => {
    if (!selectedFilters?.size) return

    const previousSearchInfo = JSON.parse(JSON.stringify(searchInfo || {}))
    let activeSearchInfo = JSON.parse(JSON.stringify(searchInfo || {}))

    selectedFilters.forEach((filter, mapKey) => {
      const { group: groupFromMapKey, val: valFromMapKey } =
        getFilterDataFromMapKey(mapKey)
      const group = filter?.group || filter?.key || groupFromMapKey
      const val = filter?.val || valFromMapKey
      const label = filter?.label || val
      const isInStockOnlyFilter = group === 'instockOnly'
      const filterType = facets.find((facet) => facet.key === group)
      const predefinedFilterValues = [...selectedFilters?.values()]?.map(filter => filter.label || filter.val)
      const nextSearchFilter = {
        ...(activeSearchInfo.searchFilter || {}),
        facetType: filterType?.label,
        deselectedFacetValues: label,
        predefinedFilterValues: predefinedFilterValues
      }

      delete nextSearchFilter['Custom Input Values']

      // Keep custom price tracking behavior aligned with setFilter.
      if (filterType?.type === 'price') {
        const isCustomInput = !filterType?.buckets?.find(
          (bucket) => bucket.label === label
        )
        if (isCustomInput) {
          nextSearchFilter['Custom Input Values'] = label
        }
      }

      delete nextSearchFilter.facetValue

      if (isInStockOnlyFilter) {
        delete nextSearchFilter.predefinedFilterValues
        delete nextSearchFilter['Custom Input Values']
        delete nextSearchFilter.facetType
      }

      activeSearchInfo = {
        ...activeSearchInfo,
        searchFilter: nextSearchFilter,
      }
      fireSearchTabsTracking({ tabType, searchInfoOverride: activeSearchInfo })
    })

    dispatch({ type: 'SEARCH_INFO', payload: previousSearchInfo })
  }

  const clearFilters = () => {
    trackFiltersBeforeClearAll()
    skipNextSearchTabsTracking.current = true
    dispatch({ type: 'CLEAR_FILTERS' })
  }

  const setFilter = (option, clearGroup) => {
    if(option?.key === 'instockOnly') {
      const isInStockFiltered = !selectedFilters.has(option?.key)
      const nextSearchFilter = {
        ...(searchInfo?.searchFilter || {}),
      }
      if(isInStockFiltered) {
        nextSearchFilter.facetValue = option.val
        delete nextSearchFilter.deselectedFacetValues
      } else {
        nextSearchFilter.deselectedFacetValues = option.val
        delete nextSearchFilter.facetValue
      }
      delete nextSearchFilter.predefinedFilterValues
      delete nextSearchFilter['Custom Input Values']
      delete nextSearchFilter.facetType
      dispatch({
        type: 'SEARCH_INFO',
        payload: {
          ...(searchInfo || {}),
          searchFilter: nextSearchFilter,
        },
      })
      return
    }
    const key = `${option.group}:${option.val}`
    const filterType = facets.find((facet) => facet.key === option.group)

    const selectedFacetGroup = origFacetDisplays[option.group] || []
    const selectedFacetValue = selectedFacetGroup.find(
      (facet) => facet.value === option.val
    )
    const searchFilterFacetKey = !!selectedFacetValue
      ? 'deselectedFacetValues'
      : 'facetValue'

    const nextSearchFilter = {
      ...((searchInfo && searchInfo.searchFilter) || {}),
      facetType: filterType?.label,
      [searchFilterFacetKey]: option.label || option.val
    }
    delete nextSearchFilter['Custom Input Values']

    // capture custom input values for price facet, since these values will not be present in buckets
    if (filterType?.type === 'price') {
      const isCustomInput = !filterType.buckets.find(
        (bucket) => bucket.label === option.label
      )

      if (isCustomInput) {
        nextSearchFilter['Custom Input Values'] = option.label
      }
    }

    if (!!selectedFacetValue) {
      delete nextSearchFilter.facetValue
    } else {
      delete nextSearchFilter.deselectedFacetValues
    }
    dispatch({
      type: 'SEARCH_INFO',
      payload: {
        ...(searchInfo || {}),
        searchFilter: nextSearchFilter,
      },
    })
    // Find and remove all queries in the selected group
    if (clearGroup) {
      selectedFilters.forEach((filter) => {
        if (filter.group == option.group) {
          selectedFilters.delete(`${filter.group}:${filter.value}`)
        }
      })
    }
    // Find and remove the selected filter if set
    if (selectedFilters.has(key)) {
      selectedFilters.delete(key)
      dispatch({ type: 'UPDATE_FILTERS', payload: selectedFilters })
      return
    }
    dispatch({
      type: 'UPDATE_FILTERS',
      payload: new Map(selectedFilters.set(key, option)),
    })
    // TODO: persist new map to url
  }

  return (
    <SearchContext.Provider
      value={{
        originalSpelling,
        correctedSpelling,
        country,
        currency,
        error,
        fusionQueryId,
        instockOnly,
        lang,
        salesOrg,
        products,
        priceInfo,
        searchSSEError,
        searchSSEComplete,
        contents,
        docs,
        numFound,
        start,
        rows,
        view,
        customSort,
        origFacetDisplays,
        pages,
        pageNumber,
        facets,
        originalFilters,
        selectedFilters,
        isSearching,
        searchProducts,
        processProducts,
        getSearchData,
        searchBrands,
        searchArticles,
        searchSolutions,
        searchSolutionsAndServices,
        searchDocs,
        clearFilters,
        setFilter,
        searchSource,
        query,
        qsrc,
        origParams,
        outOfStockOnly,
        noResultsFound,
        signalMetaData,
        tabType,
        setTabtype,
        contentData,
        dispatch,
        trackSearchTabsData
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}

function reducer(state, { type, payload }) {
  switch (type) {
    case 'INITIATED':
      return { ...state, isSearching: true, query: payload, error: false }
    case 'ERROR_RESET_SEARCH':
      return { ...state, isSearching: false, numFound: 0, error: true, query: payload }
    case 'FETCH_CONTENTS':
      return { ...state, contents: payload }
    case 'CLEAR_FILTERS':
      return { ...state, selectedFilters: new Map() }
    case 'UPDATE_FILTERS':
      return { ...state, selectedFilters: new Map(payload) }
    case 'SET_SEARCH_SOURCE':
      return { ...state, ...payload }
    case 'SET_DOCS':
      return { ...state, docs: payload }
    case 'SET_PRODUCT_VIEW':
      return { ...state, ...payload }
    case 'RESET_PAGE': {
      const tabType = payload?.tabType;
      const existingContentData = state?.contentData || {};
      const existingTabData = existingContentData?.[tabType] || {};
      const tabData = {
        ...existingTabData,
        pageNumer: initialState.pageNumber,
      };
      return {
        ...state,
        pageNumber: initialState.pageNumber,
        [tabType]: tabData
      }
    }
    /**
     * For Product Tab
     */
    case 'COMPLETED': {
      const { origParams, origFacetDisplays, products } = payload?.data
      const instockOnly = origParams?.instockOnly === 'true' ? true : false
      const customSort = origParams?.customSort
      const { rangeFacet, selectedFacet } = origParams
      const selectedFilters = new Map()
      const country = locale.split('_')[1]

      if (instockOnly) {
        selectedFilters.set('instockOnly', {
          key: 'instockOnly',
          type: 'instockOnly',
          label: t('In stock only'),
          val: instockOnly,
        })
      }
      processSelectedFacets(rangeFacet, origFacetDisplays, selectedFilters)
      processSelectedFacets(selectedFacet, origFacetDisplays, selectedFilters)
      // Generate next state
      let nextState = {
        ...state,
        customSort,
        isSearching: false,
        error: false,
        lang: locale === 'en_CA' ? 'en_US' : locale,
        locale,
        ...payload?.data,
        country,
        language: locale === 'en_CA' ? 'en_US' : locale,
        instockOnly,
        originalFilters: new Map(selectedFilters),
        salesOrg: payload.salesOrg,
        selectedFilters,
      }
      // identify out of stock only parts in search response
      // we will not receive instockOnly in origparams
      nextState = {
        ...nextState,
        outOfStockOnly: !origParams.hasOwnProperty('instockOnly'),
      }
      // Check if products are returned from search API and no facets applied
      let isEmptyContent = false
      if (state?.contents && Object.keys(state?.contents).length > 0) {
        isEmptyContent = Object.values(state.contents).every(
          (content) => content?.total === 0 || content?.numFound === 0
        )
      }

      nextState = {
        ...nextState, noResultsFound: (
          !products?.length &&
          Object.keys(origFacetDisplays).length === 0 &&
          isEmptyContent
        )
      }

      return nextState
    }
    /**
     * For Content Tabs
     */
    case 'SEARCH_CONTENT': {
      const tabType = payload?.tabType;
      const existingContentData = state?.contentData || {};
      const existingTabData = existingContentData?.[tabType] || {};
      const tabData = {
        ...existingTabData,
        isSearching: !!payload?.isSearching,
      };
      return {
        ...state,
        contentData: {
          ...existingContentData,
          [tabType]: tabData
        }
      }
    }
    case 'UPDATE_CONTENT_FILTERS': {
      const tabType = payload?.tabType;
      const existingContentData = state?.contentData || {};
      const existingTabData = existingContentData?.[tabType] || {};
      const tabData = {
        ...existingTabData,
        selectedFilters: payload?.clearAllFilters ? new Map() : new Map(payload?.selectedFilters)
      };
      return {
        ...state,
        contentData: {
          ...existingContentData,
          [tabType]: tabData
        }
      }
    }
    case 'SET_CONTENTS': {
      const { tabType, salesOrg, products, data = {}, userSegment, sessionId } = payload || {};
      const { origParams = {}, origFacetDisplays = {}, docs, numFound } = data;
      const { selectedFacet, start, rows } = origParams;
      const selectedFilters = new Map();
      const country = locale.split('_')[1];
      const lang = locale === 'en_CA' ? 'en_US' : locale;
      const pageNumber = (start / rows) + 1;
      const pages = Math.ceil(numFound / rows);

      processSelectedFacets(selectedFacet, origFacetDisplays, selectedFilters);

      let tabData = {
        // api response
        ...data,
        lang,
        locale,
        country,
        language: lang,
        userSegment,
        sessionId,
        salesOrg,
        isSearching: false,
        // filters data
        selectedFilters,
        originalFilters: new Map(selectedFilters),
        // pagination data
        pageNumber,
        start: Number(start),
        rows: Number(rows),
        pages
      }

      // Generate next state
      let nextState = {
        ...state,
        contentData: {
          ...state.contentData,
          [tabType]: tabData
        }
      }

      // If Data is not present for any tab
      let isEmptyContent = false
      if (state?.contents && Object.keys(state?.contents).length > 0) {
        isEmptyContent = Object.values(state.contents).every(
          (content) => content?.total === 0 || content?.numFound === 0
        )
      }
      const noResult = !docs?.length && !state?.products?.length && Object?.keys(origFacetDisplays)?.length === 0 && isEmptyContent
      if (!state.isSearching) {
        nextState = { ...nextState, noResultsFound: noResult }
      }

      return nextState
    }
    case 'SEARCH_INFO': {
      return {
        ...state,
        searchInfo: payload
      }
    }
  }
}

const getSelectedFiltersDiff = (previousFilters, currentFilters, facets) => {
  const prev = previousFilters instanceof Map ? previousFilters : new Map()
  const curr = currentFilters instanceof Map ? currentFilters : new Map()
  const added = []
  const removed = []

  curr.forEach((value, key) => {
    if (!prev.has(key)){
      const facetKey = value?.key || value?.group
      const facetValue = value?.label || value?.val
      if(!facetKey || !facetValue){
        return
      }
      const filteredFacet = facets.find(facet => facet.key === facetKey)
       added.push({
        facetType: filteredFacet?.label,
        facetValue: facetValue
       })
    }
  })

  prev.forEach((value, key) => {
    if (!curr.has(key)) {
      const facetKey = value?.key || value?.group
      const facetValue = value?.label || value?.val
      if(!facetKey || !facetValue){
        return
      }
      const filteredFacet = facets.find(facet => facet.key === facetKey)
      removed.push({
        facetType: filteredFacet?.label,
        deselectedFacetValues: facetValue
      })
    }
  })

  return { added, removed }
}

const processSelectedFacets = (facet, originalFacetArray, selectedFilters) => {
  if (facet) {
    const facetArray = Array.isArray(facet) ? facet : splitFacets(facet);
    facetArray.map((range) => {
      const [rKey] = range.split(':')
      originalFacetArray[rKey].map(({ key, value: val, ...rest }) => {
        selectedFilters.set(`${key}:${val}`, { key, val, ...rest });
      });
    })
  }
}
